import {
  createCodeableConcept,
  createIdentifier,
  createReference,
  getCodingsFromCodableConcepts,
  getIdentifier,
  IdentifierCode,
  isValidReference,
  LocationAcuityCode,
  LocationAcuityDescription,
  LocationCodeDescription,
  LocationTypeCode,
  type WithId,
} from '@quromedical/fhir-common';
import { compact, first } from '@quromedical/utils';
import type { Coding } from 'fhir/r4';

import {
  type CreateLocation,
  type Location,
  LocationSchema,
  type UpdateLocation,
} from './schema';

export const createFHIRLocationIdentifiers = (
  location: CreateLocation | UpdateLocation | Location,
) => {
  // NOTE: Default capacity to zero for a soft migration
  const { capacity = 0 } = location;

  return [
    createIdentifier(IdentifierCode.locationCapacity, capacity.toString()),
  ];
};

export const mapCreateModelToFhirLocation = (
  location: CreateLocation,
): fhir4.Location => {
  const {
    name,
    managingOrganization,
    status,
    type,
    description,
    parent,
    acuity,
  } = location;
  const identifier = createFHIRLocationIdentifiers(location);

  const fhirType = createCodeableConcept(
    type,
    LocationCodeDescription[type],
    LocationCodeDescription[type],
  );

  const acuityType = acuity
    ? createCodeableConcept(
        acuity,
        LocationAcuityDescription[acuity],
        LocationAcuityDescription[acuity],
      )
    : undefined;

  const organization = createReference('Organization', managingOrganization);

  const partOf = parent ? createReference('Location', parent) : undefined;

  return {
    resourceType: 'Location',
    name,
    managingOrganization: organization,
    status,
    identifier,
    type: compact([fhirType, acuityType]),
    description,
    partOf,
  };
};

export const mapUpdateModelToFhirLocation = (
  location: UpdateLocation,
): WithId<fhir4.Location> => {
  const {
    name,
    managingOrganization,
    status,
    type,
    description,
    id,
    parent,
    acuity,
  } = location;
  const identifier = createFHIRLocationIdentifiers(location);

  const fhirType = createCodeableConcept(
    type,
    LocationCodeDescription[type],
    LocationCodeDescription[type],
  );

  const acuityType = acuity
    ? createCodeableConcept(
        acuity,
        LocationAcuityDescription[acuity],
        LocationAcuityDescription[acuity],
      )
    : undefined;

  const organization = managingOrganization
    ? createReference('Organization', managingOrganization)
    : undefined;
  const partOf = parent ? createReference('Location', parent) : undefined;

  return {
    id,
    resourceType: 'Location',
    name,
    identifier,
    managingOrganization: organization,
    status,
    type: compact([fhirType, acuityType]),
    description,
    partOf,
  };
};

const isLocationTypeCode = (code: string): code is LocationTypeCode => {
  return Object.values(LocationTypeCode).includes(code as LocationTypeCode);
};

const isAcuityTypeCode = (code: string): code is LocationAcuityCode => {
  return Object.values(LocationAcuityCode).includes(code as LocationAcuityCode);
};

export const mapFhirLocationToModel = (
  fhir: WithId<fhir4.Location>,
): Location => {
  const {
    name,
    managingOrganization,
    status,
    type,
    description,
    id,
    partOf,
    identifier,
  } = fhir;

  const modelType = type
    ? getCodingsFromCodableConcepts<Coding>(type)
        ?.map((c) => c.code || '')
        .filter(isLocationTypeCode)
    : undefined;

  const acuity = type
    ? getCodingsFromCodableConcepts<Coding>(type)
        ?.map((c) => c.code || '')
        .filter(isAcuityTypeCode)
    : undefined;

  const organization = isValidReference(managingOrganization)
    ? managingOrganization
    : undefined;

  const partOfOrganization = isValidReference(partOf) ? partOf : undefined;
  // NOTE: Default capacity to zero for a soft migration
  const capacity =
    getIdentifier(IdentifierCode.locationCapacity, identifier) || '0';

  const location = {
    id,
    name,
    managingOrganization: organization,
    status,
    type: first(modelType),
    description,
    parent: partOfOrganization,
    capacity,
    acuity: first(acuity),
  };

  const parsed = LocationSchema.safeParse(location);

  if (!parsed.success) {
    throw parsed.error;
  }

  return parsed.data;
};
